import { ErrorMessage, Field, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormLabel } from '../../forms';

interface DateFieldProps {
  name: string;
  label?: string;
  isInvalid?: boolean;
  disabled?: boolean;
  max?: string;
  min?: string;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  handleSubmit?: () => void;
  default_Value?: string;
  required?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({
  name,
  label,
  isInvalid,
  disabled,
  max,
  min,
  handleSubmit,
  setFieldValue: setFieldValueProp,
  default_Value,
  required
}) => {
  const formik = useFormikContext<Record<string, unknown>>();
  const setFieldValue = setFieldValueProp ?? formik?.setFieldValue;

  const debouncedSubmit = useCallback(
    debounce(() => {
      if (!isInvalid && typeof handleSubmit === 'function') {
        handleSubmit();
      }
    }, 1000),
    [handleSubmit, isInvalid]
  );

  const [dateValue, setDateValue] = useState('');

  useEffect(() => {
    if (default_Value == null || default_Value === '') return;

    const date = new Date(default_Value);
    if (Number.isNaN(date.getTime())) return;

    setDateValue(date.toISOString().split('T')[0]);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDateValue(newValue);
    if (setFieldValue) {
      setFieldValue(name, newValue);
    }
    if (typeof handleSubmit === 'function') {
      debouncedSubmit();
    }
  };

  return (
    <Form.Group
      controlId={name}
      className="w-100"
    >
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <Field name={name}>
        {({ field }: { field: any }) => (
          <Form.Control
            type="date"
            max={max}
            min={min}
            {...field}
            disabled={disabled}
            isInvalid={isInvalid}
            defaultValue={default_Value}
            value={dateValue}
            onChange={handleChange}
            aria-required={required || undefined}
          />
        )}
      </Field>

      <ErrorMessage
        name={name}
        component="span"
        className="text-danger fs-small"
      />
    </Form.Group>
  );
};
